import { createSlice } from "@reduxjs/toolkit";

import { addWebSocketCases } from "../helpers/storeAction";
import {
  addNewDocumentData,
  addNewPortCallData,
  deleteDocumentData,
  deletePortCall,
  fetchPortCalls,
  getPortCallingListData,
  updatePortCall,
} from "../thunks/portCallThunks";

import type { PortCall } from "../../utils/types/portCalling";

interface PortCallsListState {
  portCalls: PortCall[];
  loading: boolean;
  error: string | null;
  isDataFetched?: boolean;
}

const initialState: PortCallsListState = {
  portCalls: [],
  loading: false,
  error: null,
  isDataFetched: false,
};

export const portCallListSlice = createSlice({
  name: "portCalls",
  initialState,
  reducers: {
    resetDataFetched: (state) => {
      state.isDataFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortCalls.pending, (state) => {
        state.loading = true;
        state.isDataFetched = true;
      })
      .addCase(fetchPortCalls.fulfilled, (state, action) => {
        state.portCalls = action.payload || [];
        state.loading = false;
      })
      .addCase(fetchPortCalls.rejected, (state) => {
        state.error = "Unknown error… Please try again";
        state.isDataFetched = false;
        state.loading = false;
      });
    builder
      .addCase(getPortCallingListData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPortCallingListData.fulfilled, (state, action) => {
        state.portCalls = action.payload || [];
        state.loading = false;
      })
      .addCase(getPortCallingListData.rejected, (state) => {
        state.error = "Unknown error… Please try again";
        state.loading = false;
      });

    builder
      .addCase(updatePortCall.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePortCall.fulfilled, (state, action) => {
        state.loading = false;
        state.portCalls = state.portCalls.map((call) => {
         
          if (call.portCallingsId === action.payload?.portCallingsId) {
            return { ...call, ...action.payload };
          }
          return call;
        });
      })
      .addCase(updatePortCall.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(deletePortCall.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePortCall.fulfilled, (state, action) => {
        state.loading = false;
        state.portCalls = state.portCalls.filter(
          (call) => call.portCallingsId !== action.payload?.id
        );
      })
      .addCase(deletePortCall.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(addNewPortCallData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNewPortCallData.fulfilled, (state, action) => {
        state.loading = false;
        state.portCalls = [action.payload, ...state.portCalls];
      })
      .addCase(addNewPortCallData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(deleteDocumentData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDocumentData.fulfilled, (state, action) => {
        state.loading = false;
        state.portCalls = state.portCalls.map((call) => {
          console.log(action.payload);
          if (call.portCallingsId === action.payload?.portCallingsId) {
            return {
              ...call,
              document:
                call.document?.filter(
                  (doc) => doc.documentId !== action.payload?.id
                ) || [],
            };
          }
          return call;
        });
      })
      .addCase(deleteDocumentData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });
    builder
      .addCase(addNewDocumentData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNewDocumentData.fulfilled, (state, action) => {
        state.loading = false;
        const payloadAny = action.payload as any;
        if (!payloadAny) return;

        // Case A: thunk returns { portCallingsId, data: document }
        if (payloadAny.portCallingsId && payloadAny.data) {
          const portCallingsId = payloadAny.portCallingsId;
          const doc = payloadAny.data;
          state.portCalls = state.portCalls.map((call) => {
            if (String(call.portCallingsId) === String(portCallingsId)) {
              return { ...call, document: [doc, ...(call.document || [])] };
            }
            return call;
          });
          return;
        }

        // Case B: some APIs may return the updated portCall object directly
        const maybeUpdatedPortCall = payloadAny.data && payloadAny.data.portCallingsId ? payloadAny.data : payloadAny;
        if (maybeUpdatedPortCall && maybeUpdatedPortCall.portCallingsId) {
          const gid = maybeUpdatedPortCall.portCallingsId || maybeUpdatedPortCall.id;
          state.portCalls = state.portCalls.map((call) =>
            String(call.portCallingsId) === String(gid) ? { ...call, ...maybeUpdatedPortCall } : call
          );
        }
      })
      .addCase(addNewDocumentData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });
      addWebSocketCases(builder, "portCalls", "portCalls")
  },
});

export const { resetDataFetched } = portCallListSlice.actions;
export default portCallListSlice.reducer;
